/**
 * PrepareInvoiceModal — the "Prepare invoice" dialog from the Engagement Card - Projects mockup.
 *
 * The invoice is built from the PARTICULARS of what the customer accepted (GET .../invoices/prepare-context):
 * every line carries its state — still available, or already invoiced on INV-00x (shown, not selectable) —
 * so a later invoice only ever bills what is left. Extra work goes in as additional line items. Tax is
 * line-level (tax code per line, one tenant rate). Beneath the lines, "This invoice" and "Invoice
 * summary" show the running position against the accepted value, with the tenant's warn / block rule.
 * Creates the invoice via POST /api/v1/engagements/{id}/invoices.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/UI/Button';
import { Input } from '@/components/UI/Input';
import { Modal } from '@/components/UI/Modal';
import { Select } from '@/components/UI/Select';
import { LoadingState } from '@/components/UI/LoadingState';
import {
  useCreateInvoiceMutation, useGetInvoicePrepareContextQuery,
} from '@/redux/internal-services/jobsApi';
import type { InvoicePrepareLine } from '@/redux/internal-services/jobsApi';
import { localDate } from '@/utils/isoLocalDateTime';

interface PrepareInvoiceModalProps {
  engagementId: string;
  billableAmount: number | null;
  previouslyInvoiced: number;
  onClose: () => void;
  onCreated: () => void;
}

interface AddedLine { id: number; name: string; amount: string; taxCode: string }

// `value` / `label` is what the server stores (invoice_type / payment_terms); only the shown text is translated.
const INVOICE_TYPES = [
  { value: 'Final invoice — remaining balance', key: 'final' },
  { value: 'Progress invoice — partial amount', key: 'progress' },
  { value: 'Deposit invoice', key: 'deposit' },
];
const TERMS: { label: string; key: string; days: number | null }[] = [
  { label: 'Net 15', key: 'net15', days: 15 }, { label: 'Net 30', key: 'net30', days: 30 }, { label: 'Net 45', key: 'net45', days: 45 },
  { label: 'Net 60', key: 'net60', days: 60 }, { label: 'Due on receipt', key: 'onReceipt', days: 0 }, { label: 'Custom date', key: 'custom', days: null },
];
const TAXABLE = new Set(['TX-MAT', 'TX-SRV']);

const money = (n: number) => `${n < 0 ? '−' : ''}$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const r2 = (n: number) => Math.round(n * 100) / 100;
// Quantities are whole hundredths, like amounts (the server reports `remaining_quantity` at 2 decimals too).
const qty = (n: number) => String(r2(n));

const eyebrow = 'text-micro font-bold uppercase tracking-[0.08em] text-ui-text3';

export function PrepareInvoiceModal({
  engagementId, billableAmount, previouslyInvoiced, onClose, onCreated,
}: PrepareInvoiceModalProps) {
  const { t } = useTranslation();
  const { data: ctx, isLoading: loadingCtx } = useGetInvoicePrepareContextQuery(engagementId, { refetchOnMountOrArgChange: true });
  const [createInvoice, { isLoading }] = useCreateInvoiceMutation();

  const [invoiceType, setInvoiceType] = useState(INVOICE_TYPES[0].value);
  const [terms, setTerms] = useState('Net 30');
  const [customDue, setCustomDue] = useState('');
  // line_id -> quantity being invoiced now (as typed). Present = the line is ticked.
  const [selected, setSelected] = useState<Record<string, string>>({});
  const [added, setAdded] = useState<AddedLine[]>([]);
  const [draft, setDraft] = useState<AddedLine>({ id: 0, name: '', amount: '', taxCode: 'TX-SRV' });
  const [manualSubtotal, setManualSubtotal] = useState('');
  const [error, setError] = useState<string | null>(null);

  const lines: InvoicePrepareLine[] = useMemo(() => ctx?.lines ?? [], [ctx]);
  const available = useMemo(() => lines.filter((l) => !l.fully_invoiced), [lines]);
  const hasLines = lines.length > 0;

  // Default selection: everything not already invoiced (the "seeded" amount).
  const defaults = (ls: InvoicePrepareLine[]) => Object.fromEntries(ls.filter((l) => !l.fully_invoiced).map((l) => [l.line_id, qty(l.remaining_quantity)]));
  const reset = () => { setSelected(defaults(lines)); setAdded([]); };
  useEffect(() => { if (ctx) setSelected(defaults(ctx.lines)); }, [ctx]); // eslint-disable-line react-hooks/exhaustive-deps

  /** The quantity being invoiced, clamped to what is left, and the part of the line's amount it is.
   *  Taking everything that is left takes the remaining amount exactly (no rounding drift). */
  const take = (l: InvoicePrepareLine, typed: string | undefined) => {
    const left = r2(l.remaining_quantity);
    const typedQ = Math.min(Math.max(parseFloat(typed ?? '') || 0, 0), left);
    const all = typedQ >= left - 0.005;
    const q = all ? left : r2(typedQ);
    const amount = all ? l.remaining_amount : r2(l.remaining_amount * q / (left || 1));
    return { q, amount };
  };

  const pct = ctx?.tax_percent ?? 0;
  const accepted = ctx?.accepted_value ?? billableAmount;
  const previous = ctx?.previously_invoiced ?? previouslyInvoiced;

  const picked = lines.filter((l) => l.line_id in selected).map((l) => ({ line: l, ...take(l, selected[l.line_id]) })).filter((p) => p.q > 0);
  const selLines = picked.map((p) => p.line);
  const subtotal = hasLines ? r2(picked.reduce((s, p) => s + p.amount, 0)) : (parseFloat(manualSubtotal) || 0);
  const addlTotal = r2(added.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0));
  const taxableBase = picked.filter((p) => TAXABLE.has(p.line.tax_code)).reduce((s, p) => s + p.amount, 0)
    + added.filter((l) => TAXABLE.has(l.taxCode)).reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);
  // Same rule as the server (BillingService.checked_totals): taxable lines + taxable additional lines
  // at the tenant rate, also when there are no estimate lines (the manual subtotal has no tax code).
  const tax = r2(taxableBase * pct / 100);
  const total = r2(subtotal + addlTotal + tax);

  const availableTotal = r2(available.reduce((s, l) => s + l.remaining_amount, 0) + available.filter((l) => TAXABLE.has(l.tax_code)).reduce((s, l) => s + l.remaining_amount, 0) * pct / 100);
  const balanceBefore = accepted != null ? r2(accepted - previous) : null;
  const balanceAfter = balanceBefore != null ? r2(balanceBefore - total) : null;
  const over = accepted != null ? r2(previous + total - accepted) : 0;
  const mode = ctx?.validation_mode ?? 'warn';
  const blocked = over > 0.005 && mode === 'block';

  const dueDate = useMemo(() => {
    const term = TERMS.find((x) => x.label === terms);
    if (term?.days == null) return customDue;
    const d = new Date(); d.setDate(d.getDate() + term.days);
    return localDate(d);
  }, [terms, customDue]);
  const dueText = dueDate ? new Date(`${dueDate}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

  const toggle = (l: InvoicePrepareLine) => setSelected((prev) => {
    const n = { ...prev };
    if (l.line_id in n) delete n[l.line_id]; else n[l.line_id] = qty(l.remaining_quantity);
    return n;
  });
  const setQty = (id: string, value: string) => setSelected((prev) => ({ ...prev, [id]: value }));
  const allOn = available.length > 0 && available.every((l) => l.line_id in selected);
  const toggleAll = () => setSelected(allOn ? {} : defaults(lines));

  const addDraft = () => {
    if (!draft.name.trim() || !(parseFloat(draft.amount) > 0)) return;
    setAdded((prev) => [...prev, { ...draft, id: Date.now() }]);
    setDraft({ id: 0, name: '', amount: '', taxCode: draft.taxCode });
  };

  async function handleCreate() {
    setError(null);
    if (total <= 0) { setError(t('billing.prepare.errorZero', 'Invoice total must be greater than zero.')); return; }
    if (blocked) { setError(t('billing.prepare.errorBlocked', 'This invoice would take cumulative invoicing past the accepted value, and your company settings block that.')); return; }
    try {
      await createInvoice({
        engagementId,
        invoice_type: invoiceType,
        payment_terms: terms === 'Custom date' ? 'Custom' : terms,
        due_date: dueDate || undefined,
        lines: picked.map(({ line: l, q, amount }) => ({
          description: l.name, amount, quantity: q, unit: l.unit, unit_price: l.unit_price,
          tax_code: l.tax_code, source_line_id: l.line_id,
        })),
        additional_lines: added.map((l) => ({
          description: l.name, amount: parseFloat(l.amount) || 0, quantity: 1, unit: 'ls',
          unit_price: parseFloat(l.amount) || 0, tax_code: l.taxCode,
        })),
        subtotal,
        tax,
        previously_invoiced: previous,
        amount: total,
      }).unwrap();
      onCreated();
    } catch (e) {
      const detail = (e as { data?: { detail?: string } })?.data?.detail;
      setError(detail || t('billing.prepare.errorCreate', 'Failed to create invoice. Please try again.'));
    }
  }

  return (
    <Modal
      title={t('billing.prepare.title', 'Prepare invoice')}
      onClose={onClose}
      maxWidthClassName="max-w-2xl"
      closeOnBackdrop={false}
      onSave={() => void handleCreate()}
      saveLabel={t('billing.prepare.submit', 'Create invoice & submit for review')}
      saving={isLoading}
      saveDisabled={total <= 0 || blocked || loadingCtx}
    >
      {loadingCtx ? <LoadingState /> : (
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="space-y-1.5">
              <span className={eyebrow}>{t('billing.prepare.invoiceType', 'Invoice type')}</span>
              <Select value={invoiceType} onChange={(e) => setInvoiceType(e.target.value)}>
                {INVOICE_TYPES.map((it) => <option key={it.value} value={it.value}>{t(`billing.prepare.type.${it.key}`, it.value)}</option>)}
              </Select>
            </label>
            <div className="space-y-1.5">
              <label className="block space-y-1.5">
                <span className={eyebrow}>{t('billing.prepare.paymentTerms', 'Payment terms')}</span>
                <Select value={terms} onChange={(e) => setTerms(e.target.value)}>
                  {TERMS.map((term) => <option key={term.label} value={term.label}>{t(`billing.prepare.terms.${term.key}`, term.label)}</option>)}
                </Select>
              </label>
              {terms === 'Custom date'
                ? <Input type="date" value={customDue} onChange={(e) => setCustomDue(e.target.value)} aria-label={t('billing.prepare.dueDate', 'Due date')} />
                : <p className="text-caption text-ui-text3">{t('billing.prepare.due', 'Due {{date}}', { date: dueText })}</p>}
            </div>
          </div>

          {hasLines && (
            <div className="flex flex-col items-start gap-2 rounded-control border border-ui-border bg-ui-subtle px-3 py-2 sm:flex-row sm:gap-3">
              <p className="flex-1 text-caption text-ui-text2">
                {t('billing.prepare.seeded', 'Seeded with the {{amount}} remaining uninvoiced amount, which excludes everything already invoiced.', { amount: money(availableTotal) })}
              </p>
              <Button type="button" variant="outline" size="xs" onClick={reset}>{t('billing.prepare.resetAmount', 'Reset amount')}</Button>
            </div>
          )}

          {hasLines ? (
            <div className="space-y-2">
              <p className={eyebrow}>{t('billing.prepare.invoiceLines', 'Invoice lines')}</p>
              <div className="overflow-hidden rounded-control border border-ui-border">
                <div className="flex items-center gap-2 bg-ui-subtle px-3 py-2 text-micro font-bold uppercase tracking-[0.08em] text-ui-text3">
                  <input type="checkbox" checked={allOn} onChange={toggleAll} aria-label={t('billing.prepare.selectAll', 'Select all available lines')} className="h-4 w-4 flex-shrink-0 accent-accent-fill" />
                  <span className="flex-1">{t('billing.prepare.col.particulars', 'Particulars')}</span>
                  <span className="hidden w-12 text-right sm:block">{t('billing.prepare.col.unit', 'Unit')}</span>
                  <span className="hidden w-16 text-right sm:block">{t('billing.prepare.col.qty', 'Qty')}</span>
                  <span className="w-20 text-right">{t('billing.prepare.col.extPrice', 'Ext. price')}</span>
                  <span className="hidden w-16 text-right sm:block">{t('billing.prepare.col.taxCode', 'Tax code')}</span>
                </div>
                {lines.map((l) => {
                  const done = l.fully_invoiced;
                  const partly = !done && l.invoiced_quantity > 0;
                  const on = !done && l.line_id in selected;
                  const taking = on ? take(l, selected[l.line_id]) : null;
                  return (
                    <div key={l.line_id} className={`flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-ui-border px-3 py-2 ${done ? 'bg-ui-subtle/60 text-ui-text3' : ''}`}>
                      <input
                        type="checkbox" checked={on} disabled={done}
                        onChange={() => toggle(l)} aria-label={l.name}
                        className="h-4 w-4 flex-shrink-0 accent-accent-fill disabled:opacity-40"
                      />
                      {/* The name keeps a readable width; on a phone the figures drop to their own line
                          instead of squeezing it to one word per row. */}
                      <div className="min-w-0 flex-1 basis-44">
                        <p className={`break-words text-xs font-semibold ${done ? '' : 'text-ui-text'}`}>{l.name}</p>
                        <p className="text-micro text-ui-text3 sm:hidden">{l.tax_code}</p>
                        {done && <p className="text-micro text-warning-text">{t('billing.prepare.fullyInvoiced', 'Fully invoiced on {{invoices}}', { invoices: l.invoiced_on.join(', ') })}</p>}
                        {partly && (
                          <p className="text-micro text-warning-text">
                            {t('billing.prepare.partlyInvoiced', '{{done}} of {{total}} already invoiced on {{invoices}} · {{left}} left', {
                              done: qty(l.invoiced_quantity), total: qty(l.quantity), invoices: l.invoiced_on.join(', '), left: qty(l.remaining_quantity),
                            })}
                          </p>
                        )}
                      </div>
                      <div className="ml-auto flex items-center gap-2">
                      <span className="w-12 text-right text-xs">{l.unit}</span>
                      {on ? (
                        <input
                          type="number" min="0" step="any" max={l.remaining_quantity}
                          value={selected[l.line_id]} onChange={(e) => setQty(l.line_id, e.target.value)}
                          aria-label={t('billing.prepare.qtyAria', '{{name}} — quantity to invoice (up to {{max}})', { name: l.name, max: qty(l.remaining_quantity) })}
                          className="h-8 w-16 rounded-md border border-ui-border bg-ui-input px-1.5 text-right text-xs text-ui-text focus:border-ui-ring focus:outline-none"
                        />
                      ) : (
                        <span className="w-16 text-right text-xs">{qty(done ? l.quantity : l.remaining_quantity)}</span>
                      )}
                      <span className="w-20 text-right text-xs font-semibold">{money(taking ? taking.amount : done ? l.amount : l.remaining_amount)}</span>
                      <span className="hidden w-16 text-right text-micro sm:block">{l.tax_code}</span>
                      </div>
                    </div>
                  );
                })}
                <div className="flex items-center gap-2 border-t border-ui-border bg-ui-subtle px-3 py-2.5">
                  <b className="flex-1 text-xs text-ui-text">{t('billing.prepare.subtotalItems', 'Subtotal · {{n}} of {{total}} items', { n: selLines.length, total: lines.length })}</b>
                  <b className="text-sm text-ui-text">{money(subtotal)}</b>
                </div>
              </div>
            </div>
          ) : (
            <label className="block space-y-1.5">
              <span className={eyebrow}>{t('billing.prepare.manualSubtotal', 'Invoice subtotal ($)')}</span>
              <Input type="number" min="0" step="0.01" value={manualSubtotal} onChange={(e) => setManualSubtotal(e.target.value)} placeholder="0.00" />
              <span className="block text-caption text-ui-text3">{t('billing.prepare.noLines', 'No accepted estimate lines were found for this job, so enter the amount directly.')}</span>
            </label>
          )}

          <div className="space-y-2">
            <div className="overflow-hidden rounded-control border border-ui-border">
              <div className="bg-ui-subtle px-3 py-2 text-micro font-bold uppercase tracking-[0.08em] text-ui-text3">{t('billing.prepare.additional', 'Additional line items')}</div>
              {added.map((l) => (
                <div key={l.id} className="flex items-center gap-2 border-t border-ui-border px-3 py-2">
                  <span className="min-w-0 flex-1 break-words text-xs font-semibold text-ui-text">{l.name}</span>
                  <span className="w-20 text-right text-xs font-semibold">{money(parseFloat(l.amount) || 0)}</span>
                  <span className="hidden w-16 text-right text-micro text-ui-text3 sm:block">{l.taxCode}</span>
                  <button type="button" aria-label={t('billing.prepare.removeLine', 'Remove line')} onClick={() => setAdded((p) => p.filter((x) => x.id !== l.id))} className="focus-ring rounded-full px-1.5 text-ui-text3 hover:text-danger-text">×</button>
                </div>
              ))}
              {/* One aligned form row: the particular takes the width, amount and tax code sit side by side
                  (two columns on a phone, one row from sm up), Add closes the row. Input/Select wrap
                  themselves in a block, so they are laid out by a grid, never by flex-wrap. */}
              <div className="grid grid-cols-2 items-center gap-2 border-t border-ui-border px-3 py-3 sm:grid-cols-[minmax(0,1fr)_7rem_8rem_auto]">
                <div className="col-span-2 sm:col-span-1">
                  <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder={t('billing.prepare.particularPlaceholder', 'Particular — e.g. after-hours crew callout')} aria-label={t('billing.prepare.particular', 'Particular')} />
                </div>
                <Input type="number" min="0" step="0.01" value={draft.amount} onChange={(e) => setDraft({ ...draft, amount: e.target.value })} placeholder={t('billing.prepare.amount', 'Amount')} aria-label={t('billing.prepare.amount', 'Amount')} />
                <Select value={draft.taxCode} onChange={(e) => setDraft({ ...draft, taxCode: e.target.value })} aria-label={t('billing.prepare.col.taxCode', 'Tax code')}>
                  {(ctx?.tax_codes ?? ['TX-MAT', 'TX-SRV', 'TX-EXEMPT']).map((c) => <option key={c} value={c}>{c}</option>)}
                </Select>
                <Button type="button" variant="outline" size="md" className="col-span-2 sm:col-span-1" onClick={addDraft} disabled={!draft.name.trim() || !(parseFloat(draft.amount) > 0)}>{t('billing.prepare.add', '+ Add')}</Button>
              </div>
            </div>
          </div>

          <div className="overflow-hidden rounded-control border border-ui-border">
            <div className="bg-ui-subtle px-3 py-2 text-micro font-bold uppercase tracking-[0.08em] text-ui-text3">{t('billing.prepare.thisInvoice', 'This invoice')}</div>
            <Row label={t('billing.prepare.selectedLines', 'Selected lines · {{n}} of {{total}} items', { n: selLines.length, total: lines.length })} value={money(subtotal)} />
            <Row label={t('billing.prepare.additional', 'Additional line items')} value={money(addlTotal)} />
            <Row label={pct ? t('billing.prepare.taxPct', 'Tax · {{pct}}% on taxable lines', { pct: qty(pct) }) : t('billing.prepare.tax', 'Tax')} value={money(tax)} />
            <div className="flex items-center gap-3 border-t border-ui-border bg-ui-subtle px-3 py-2.5">
              <b className="flex-1 text-sm text-ui-text">{t('billing.prepare.amountNow', 'Amount to be invoiced now')}</b>
              <b className="text-base text-accent-text">{money(total)}</b>
            </div>
            {(ctx?.previous_invoices?.length ?? 0) > 0 && (
              <div className="flex gap-3 border-t border-ui-border px-3 py-2 text-caption text-ui-text3">
                <span className="flex-1">{t('billing.prepare.previousRef', 'Previously invoiced · {{invoices}}, for reference only, not included above', { invoices: ctx!.previous_invoices.map((p) => p.number).join(', ') })}</span>
                <span>{money(previous)}</span>
              </div>
            )}
          </div>

          {accepted != null && (
            <div className="rounded-control border border-ui-border bg-ui-subtle px-3.5 py-3">
              <p className={`${eyebrow} mb-2`}>{t('billing.prepare.summary', 'Invoice summary')}</p>
              <SummaryRow label={t('billing.prepare.acceptedValue', 'Total accepted value')} value={money(accepted)} />
              <SummaryRow label={t('billing.prepare.previousInvoices', 'Previous invoices')} value={previous > 0 ? `${money(previous)} · ${ctx?.previous_invoices.map((p) => p.number).join(', ')}` : money(0)} />
              <SummaryRow label={t('billing.prepare.balanceBefore', 'Balance before this invoice')} value={money(balanceBefore ?? 0)} />
              <SummaryRow label={t('billing.prepare.thisAmount', 'This invoice amount')} value={money(total)} />
              <SummaryRow label={t('billing.prepare.balanceAfter', 'Balance to invoice after this')} value={money(balanceAfter ?? 0)} last />
              {over > 0.005 && mode !== 'off' && (
                <p className="mt-2.5 rounded-control border border-warning/40 bg-warning/10 px-2.5 py-2 text-caption text-warning-text">
                  {t('billing.prepare.over', 'Cumulative invoicing exceeds the accepted value by {{amount}}.', { amount: money(over) })}{' '}
                  {mode === 'block'
                    ? t('billing.prepare.overBlock', 'The company setting is Block, so this invoice cannot be submitted.')
                    : t('billing.prepare.overWarn', 'The company setting is Warn and Allow, so it can still be submitted — the reviewer will see the overage.')}
                </p>
              )}
            </div>
          )}

          {error && <p className="text-caption text-danger-text">{error}</p>}
        </div>
      )}
    </Modal>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-3 border-t border-ui-border px-3 py-2 text-xs text-ui-text2">
      <span className="flex-1">{label}</span><b className="text-ui-text">{value}</b>
    </div>
  );
}

function SummaryRow({ label, value, last = false }: { label: string; value: string; last?: boolean }) {
  return (
    <div className={`flex gap-3 py-1.5 ${last ? '' : 'border-b border-dashed border-ui-border'}`}>
      <span className="flex-1 text-xs text-ui-text2">{label}</span>
      <span className="text-right text-xs font-semibold text-ui-text">{value}</span>
    </div>
  );
}
